import { Injectable, inject } from '@angular/core';
import { debounceTime, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { HistoryService } from './history.service';
import { HistoryServiceV2 } from './history.service-v2';
import { HistoryChangeAction } from './history.model';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { HistoryFacade } from './history.facade';

import type { ThemeSettingsState } from '@sites/site-theme/src/state';
import { ThemeStateService } from '@sites/site-theme/src/state';
import BlocksActions from '@web/store/page-structure/blocks/blocks.actions';
import SectionsActions from '@web/store/page-structure/sections/sections.actions';
import ColumnsActions from '@web/store/page-structure/columns/columns.actions';
import GridsActions from '@web/store/page-structure/grids/grids.actions';
import WidgetsLegacyActions, { WidgetsActions } from '@web/store/page-structure/widgets/widgets.actions';
import TabsActions from '@web/store/page-structure/tabs/tabs.actions';
import PagesActions from '@web/store/website/pages/pages.actions';
import HistoryActions from './history.actions';
import { Store } from '@ngrx/store';

@Injectable()
export class HistoryEffects {
    private readonly actions$ = inject(Actions);
    private readonly themeState = inject(ThemeStateService);
    private readonly historyService = inject(HistoryService);
    private readonly historyServiceV2 = inject(HistoryServiceV2);
    private readonly historyFacade = inject(HistoryFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly store: Store = inject(Store);

    public readonly undo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HistoryActions.undoRequest),
            withLatestFrom(this.historyFacade.lastPrev$, (_, lastPrev) => lastPrev),
            filter((lastPrev) => Boolean(lastPrev)),
            map((lastPrev) => {
                if (lastPrev.action === HistoryChangeAction.batch) {
                    this.historyServiceV2.handleUndo(lastPrev);
                } else {
                    this.historyService.handleUndo(lastPrev);
                }
                return HistoryActions.undoSuccess();
            })
        )
    );

    public readonly redo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(HistoryActions.redoRequest),
            withLatestFrom(this.historyFacade.lastNext$, (_, lastNext) => lastNext),
            filter((lastNext) => Boolean(lastNext)),
            map((lastNext) => {
                if (lastNext.action === HistoryChangeAction.batch) {
                    this.historyServiceV2.handleRedo(lastNext);
                } else {
                    this.historyService.handleRedo(lastNext);
                }
                return HistoryActions.redoSuccess();
            })
        )
    );

    public readonly createBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.blockCreated),
            debounceTime(500),
            concatLatestFrom(() => this.blocksFacade.structure$),
            map(([{ index, block }, structure]) => {
                const blockEntity = structure.find((entity) => block.id === entity.id);
                return HistoryActions.addChangeToHistory({
                    change: this.historyService.createAddHistoryChange({
                        entity: blockEntity,
                        entityType: 'block',
                        index,
                    }),
                });
            })
        )
    );

    public readonly copyBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.blockCopied),
            debounceTime(500),
            map(({ index, copiedBlockEntity }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createCopyHistoryChange({
                        entity: copiedBlockEntity,
                        entityType: 'block',
                        index,
                    }),
                })
            )
        )
    );

    public readonly removeBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.removeBlock),
            debounceTime(500),
            filter(({ skipHistory }) => !skipHistory),
            map(({ block, index }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createRemoveHistoryChange({
                        entity: block,
                        entityType: 'block',
                        index,
                    }),
                })
            )
        )
    );

    public readonly moveBlockDown$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.moveDown),
            filter(({ skipHistory }) => !skipHistory),
            debounceTime(500),
            map(({ index }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createMoveHistoryChange({
                        positionFrom: index,
                        positionTo: index + 1,
                        entityType: 'block',
                    }),
                })
            )
        )
    );

    public readonly moveBlockUp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.moveUp),
            filter(({ skipHistory }) => !skipHistory),
            map(({ index }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createMoveHistoryChange({
                        positionFrom: index,
                        positionTo: index - 1,
                        entityType: 'block',
                    }),
                })
            )
        )
    );

    public readonly addBlockUpdateChangeToHistory$ = createEffect(() => {
        let beforeUpdate = null;
        return this.actions$.pipe(
            ofType(HistoryActions.addBlockUpdateChangeToHistory),
            tap(({ before }) => {
                if (beforeUpdate) {
                    return;
                }

                beforeUpdate = before;
            }),
            debounceTime(500),
            map(({ after }) => {
                const change = this.historyService.createUpdateHistoryChange({
                    entityType: 'block',
                    before: beforeUpdate,
                    after,
                });
                beforeUpdate = null;

                return HistoryActions.addChangeToHistory({ change });
            })
        );
    });

    // END BLOCK

    public readonly createSection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SectionsActions.sectionCreated),
            filter(({ skipHistory }) => !skipHistory),
            map(({ index, section }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createAddHistoryChange({
                        entity: section,
                        entityType: 'section',
                        index,
                    }),
                })
            )
        )
    );

    public readonly removeSection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SectionsActions.removeSection),
            filter(({ skipHistory }) => !skipHistory),
            map(({ index, section }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createRemoveHistoryChange({
                        entity: section,
                        entityType: 'section',
                        index,
                    }),
                })
            )
        )
    );

    // END SECTION

    public readonly createColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.columnCreated),
            filter(({ skipHistory }) => !skipHistory),
            map(({ index, column }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createAddHistoryChange({ entity: column, entityType: 'column', index }),
                })
            )
        )
    );

    public readonly copyColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.columnCopied),
            map(({ index, columnEntity }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createCopyHistoryChange({
                        entity: columnEntity,
                        entityType: 'column',
                        index,
                    }),
                })
            )
        )
    );

    public readonly removeColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.removeColumn),
            filter(({ skipHistory }) => !skipHistory),
            map(({ column, index }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createRemoveHistoryChange({
                        entity: column,
                        entityType: 'column',
                        index,
                    }),
                })
            )
        )
    );

    public readonly moveColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ColumnsActions.columnMoved),
            filter(({ skipHistory }) => !skipHistory),
            map(({ columnId, currentIndex, previousIndex, prevSectionId, currentSectionId }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createMoveHistoryChange({
                        entityType: 'column',
                        entityId: columnId,
                        prevParentId: prevSectionId,
                        nextParentId: currentSectionId,
                        positionFrom: previousIndex,
                        positionTo: currentIndex,
                    }),
                })
            )
        )
    );

    public readonly addColumnsUpdateChangeToHistory$ = createEffect(() => {
        let beforeUpdate = null;
        return this.actions$.pipe(
            ofType(HistoryActions.addColumnsUpdateChangeToHistory),
            tap(({ before }) => {
                if (beforeUpdate) {
                    return;
                }

                beforeUpdate = before;
            }),
            debounceTime(500),
            map(({ after }) => {
                const change = this.historyService.createUpdateHistoryChange({
                    entityType: 'columns',
                    before: beforeUpdate,
                    after,
                });
                beforeUpdate = null;

                return HistoryActions.addChangeToHistory({ change });
            })
        );
    });

    public readonly addGridsUpdateChangeToHistory$ = createEffect(() => {
        let beforeUpdate = null;
        return this.actions$.pipe(
            ofType(HistoryActions.addGridsUpdateChangeToHistory),
            tap(({ before }) => {
                if (beforeUpdate) {
                    return;
                }

                beforeUpdate = before;
            }),
            debounceTime(500),
            map(({ after }) => {
                const change = this.historyService.createUpdateHistoryChange({
                    entityType: 'grids',
                    before: beforeUpdate,
                    after,
                });
                beforeUpdate = null;

                return HistoryActions.addChangeToHistory({ change });
            })
        );
    });

    // END COLUMN

    public readonly copyGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.copy),
            map(({ entity, order }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createCopyHistoryChange({
                        entity,
                        entityType: 'grid',
                        index: order,
                    }),
                })
            )
        )
    );

    public readonly addGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.created),
            filter(({ skipHistory }) => !skipHistory),
            map(({ entity, order }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createAddHistoryChange({
                        entity,
                        entityType: 'grid',
                        index: order,
                    }),
                })
            )
        )
    );

    public readonly removeGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.remove),
            filter(({ skipHistory }) => !skipHistory),
            map(({ entity, order }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createRemoveHistoryChange({
                        entity,
                        entityType: 'grid',
                        index: order,
                    }),
                })
            )
        )
    );

    public readonly addGridUpdateChangeToHistory$ = createEffect(() => {
        let beforeUpdate = null;
        return this.actions$.pipe(
            ofType(HistoryActions.addGridUpdateChangeToHistory),
            tap(({ before }) => {
                if (beforeUpdate) {
                    return;
                }

                beforeUpdate = before;
            }),
            debounceTime(500),
            map(({ after }) => {
                const change = this.historyService.createUpdateHistoryChange({
                    entityType: 'grid',
                    before: beforeUpdate,
                    after,
                });
                beforeUpdate = null;

                return HistoryActions.addChangeToHistory({ change });
            })
        );
    });

    // END STRUCTURE

    public readonly createTab$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TabsActions.created),
            filter(({ skipHistory }) => !skipHistory),
            map(({ order, entity, childEntity }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createAddHistoryChange({
                        entity: childEntity ?? entity,
                        entityType: 'tab',
                        index: order,
                    }),
                })
            )
        )
    );

    public readonly copyTab$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TabsActions.copied),
            map(({ order, entity, childEntity }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createCopyHistoryChange({
                        entity: childEntity ?? entity,
                        entityType: 'tab',
                        index: order,
                    }),
                })
            )
        )
    );

    public readonly addTabUpdateChangeToHistory$ = createEffect(() => {
        let beforeUpdate = null;
        return this.actions$.pipe(
            ofType(HistoryActions.addTabUpdateChangeToHistory),
            tap(({ before }) => {
                if (beforeUpdate) {
                    return;
                }

                beforeUpdate = before;
            }),
            debounceTime(500),
            map(({ after }) => {
                const change = this.historyService.createUpdateHistoryChange({
                    entityType: 'tab',
                    before: beforeUpdate,
                    after,
                });
                beforeUpdate = null;

                return HistoryActions.addChangeToHistory({ change });
            })
        );
    });

    public readonly createLegacyWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.widgetCreated),
            filter(({ skipHistory }) => !skipHistory),
            map(({ index, widget }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createAddHistoryChange({
                        entity: widget,
                        entityType: 'legacyWidget',
                        index,
                    }),
                })
            )
        )
    );

    public readonly createLegacyWidgetToGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.widgetCreatedToGrid),
            filter(({ skipHistory }) => !skipHistory),
            map(({ index, widget, gridCellIndex }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createAddHistoryChange({
                        entity: widget,
                        entityType: 'legacyGridWidget',
                        index,
                        gridCellIndex,
                    }),
                })
            )
        )
    );

    public readonly copyLegacyWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.copyWidget),
            map(({ index, widget }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createCopyHistoryChange({
                        entity: widget,
                        entityType: 'legacyWidget',
                        index,
                    }),
                })
            )
        )
    );

    public readonly copyLegacyWidgetToGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.copyWidgetToGrid),
            map(({ index, widget, gridCellIndex }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createCopyHistoryChange({
                        entity: widget,
                        entityType: 'legacyGridWidget',
                        index,
                        gridCellIndex,
                    }),
                })
            )
        )
    );

    public readonly removeLegacyWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.removeWidget),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widget, index }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createRemoveHistoryChange({
                        entity: widget,
                        entityType: 'legacyWidget',
                        index,
                    }),
                })
            )
        )
    );

    public readonly removeLegacyWidgetFromGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.removeWidgetFromGrid),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widget, index, gridCellIndex }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createRemoveHistoryChange({
                        entity: widget,
                        entityType: 'legacyGridWidget',
                        index,
                        gridCellIndex,
                    }),
                })
            )
        )
    );

    // Widgets record into the batch (v2) history, like grids and tabs; legacy ones above keep v1. Moves stay on the
    // shared id-only move effects below; updates come from `WebWidgetSignalFormService`.
    public readonly createWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.created),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widget, index, gridCellIndex }) =>
                HistoryActions.addBatchChangeToHistory({
                    entry: {
                        action: HistoryChangeAction.add,
                        entityType: 'widget',
                        entity: widget,
                        index,
                        gridCellIndex,
                    },
                })
            )
        )
    );

    public readonly copyWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.copied),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widget, index, gridCellIndex }) =>
                HistoryActions.addBatchChangeToHistory({
                    entry: {
                        action: HistoryChangeAction.copy,
                        entityType: 'widget',
                        entity: widget,
                        index,
                        gridCellIndex,
                    },
                })
            )
        )
    );

    public readonly removeWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.removed),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widget, index, gridCellIndex }) =>
                HistoryActions.addBatchChangeToHistory({
                    entry: {
                        action: HistoryChangeAction.remove,
                        entityType: 'widget',
                        entity: widget,
                        index,
                        gridCellIndex,
                    },
                })
            )
        )
    );

    public readonly moveWidget$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsLegacyActions.widgetMoved),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widgetId, currentIndex, previousIndex, prevColumnId, currentColumnId }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createMoveHistoryChange({
                        entityType: 'legacyWidget',
                        entityId: widgetId,
                        prevParentId: prevColumnId,
                        nextParentId: currentColumnId,
                        positionFrom: previousIndex,
                        positionTo: currentIndex,
                    }),
                })
            )
        )
    );

    public readonly moveWidgetFromGridToColumn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.moveWidgetFromGridToColumn),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widgetId, prevParentId, nextParentId, prevIndex, nextIndex, prevGridCellIndex }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createMoveHistoryChange({
                        type: 'fromGridToColumn',
                        entityType: 'legacyGridWidget',
                        entityId: widgetId,
                        prevParentId: prevParentId,
                        nextParentId: nextParentId,
                        positionFrom: prevIndex,
                        positionTo: nextIndex,
                        fromCell: prevGridCellIndex,
                    }),
                })
            )
        )
    );

    public readonly moveWidgetFromColumnToGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.moveWidgetFromColumnToGrid),
            filter(({ skipHistory }) => !skipHistory),
            map(({ widgetId, prevParentId, nextParentId, prevIndex, nextIndex, nextGridCellIndex }) =>
                HistoryActions.addChangeToHistory({
                    change: this.historyService.createMoveHistoryChange({
                        type: 'fromColumnToGrid',
                        entityType: 'legacyGridWidget',
                        entityId: widgetId,
                        prevParentId: prevParentId,
                        nextParentId: nextParentId,
                        positionFrom: prevIndex,
                        positionTo: nextIndex,
                        toCell: nextGridCellIndex,
                    }),
                })
            )
        )
    );

    public readonly moveWidgetFromGridToGrid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GridsActions.moveWidgetFromGridToGrid),
            filter(({ skipHistory }) => !skipHistory),
            map(
                ({
                    widgetId,
                    prevParentId,
                    nextParentId,
                    prevIndex,
                    nextIndex,
                    nextGridCellIndex,
                    prevGridCellIndex,
                }) =>
                    HistoryActions.addChangeToHistory({
                        change: this.historyService.createMoveHistoryChange({
                            type: 'fromGridToGrid',
                            entityType: 'legacyGridWidget',
                            entityId: widgetId,
                            prevParentId: prevParentId,
                            nextParentId: nextParentId,
                            positionFrom: prevIndex,
                            positionTo: nextIndex,
                            toCell: nextGridCellIndex,
                            fromCell: prevGridCellIndex,
                        }),
                    })
            )
        )
    );

    public readonly addWidgetUpdateChangeToHistory$ = createEffect(() => {
        let beforeUpdate = null;
        return this.actions$.pipe(
            ofType(HistoryActions.addWidgetUpdateChangeToHistory),
            tap(({ before }) => {
                if (beforeUpdate) {
                    return;
                }

                beforeUpdate = before;
            }),
            debounceTime(500),
            map(({ after }) => {
                const change = this.historyService.createUpdateHistoryChange({
                    entityType: 'legacyWidget',
                    before: beforeUpdate,
                    after,
                });
                beforeUpdate = null;

                return HistoryActions.addChangeToHistory({ change });
            })
        );
    });

    public readonly addStoreUpdateChangeToHistory$ = createEffect(() => {
        let beforeUpdate = null;
        return this.actions$.pipe(
            ofType(HistoryActions.addStoreUpdateChangeToHistory),
            tap(({ before }) => {
                if (beforeUpdate) {
                    return;
                }

                beforeUpdate = before;
            }),
            debounceTime(500),
            map(({ after }) => {
                const change = this.historyService.createUpdateHistoryChange({
                    entityType: 'store',
                    before: beforeUpdate,
                    after,
                });
                beforeUpdate = null;

                return HistoryActions.addChangeToHistory({ change });
            })
        );
    });

    // END WIDGET

    // A burst of theme edits is one undo step: each part keeps what it was before the first edit
    // that touched it and what the last one left.
    public readonly changeTheme$ = createEffect(() => {
        let before: ThemeSettingsState | null = null;
        let after: ThemeSettingsState = {};
        return this.themeState.changes$.pipe(
            tap((change) => {
                before = { ...change.before, ...before };
                after = { ...after, ...change.after };
            }),
            debounceTime(500),
            map(() => {
                const historyChange = this.historyService.createThemeUpdateHistoryChange({
                    entityType: 'theme',
                    before,
                    after,
                });
                before = null;
                after = {};

                return HistoryActions.addChangeToHistory({ change: historyChange });
            })
        );
    });

    private readonly navigateSitePage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.navigateSitePageFromDrawer, PagesActions.addNewPage, PagesActions.deletePageRequest),
            map(() => {
                return HistoryActions.resetHistory();
            })
        )
    );
}
